import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { isBefore, format } from 'date-fns';
import { Button, ExpandCollapse, FlexGrid, Select, StackView, Tags, Typography } from '@telus-uds/components-web';
import PropTypes from 'prop-types';
import ManagedDatePicker from '../common/ManagedDatePicker';
import { useLocaleContext } from '../context/LocaleContext';
import EN from '../../EN.json';
import FR from '../../FR.json';
import { FILTERS_INIT_STATE } from './constants';
import { Box } from '../design/css';

const Filters = ({
  onApplyFilters,
  onResetFilters,
  role,
  assignedUsers,
  defaultFilters
}) => {
  const local = useLocaleContext();
  const {
    ORDERS: { filterByReportedDate },
    DEFECT_MANAGEMENT: {
      Showing_Results_for,
      FILTER = '',
      FILTER_BY_WEBFORM = {},
      FILTER_BY_CATEGORY = {},
      FILTER_BY_STATUS = {},
      FILTER_BY_ASSIGNEE = {},
      FILTER_BY_DEFECT_CATEGORY_OPTIONS,
      APPLY = '',
      RESET = '',
      STATUS_OPTIONS,
      WEBFORM_OPTIONS,
      FROM,
      TO,
      filterKeyToShortTitleMap
    } = {}
  } = local === 'en-US' ? EN : FR;

  const [filter, setFilter] = useState({
    ...FILTERS_INIT_STATE,
    ...defaultFilters
  });

  useEffect(() => {
    setFilter({ ...filter, ...defaultFilters });
  }, [defaultFilters]);

  const onRemoveTagFilter = key => {
    const tempFilters = filter;
    delete tempFilters[key];
    setFilter(tempFilters);
    onApplyFilters(tempFilters);
  };

  return (
    <StackView space={3}>
      <ExpandCollapse initialOpen={[]} tag="h2">
      {(expandProps) => (
        <ExpandCollapse.Panel
          {...expandProps}
          panelId="filters" control={<Typography heading="h3" variant={{ size: 'h3', bold: true }}>{FILTER}</Typography>}>
          <StackView space={1}>
            <FlexGrid>
              <FlexGrid.Row Style="margin-bottom:10px">
                <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                  <Select
                    label={FILTER_BY_WEBFORM.title}
                    placeholder={FILTER_BY_WEBFORM.placeholder}
                    onChange={(value) =>
                      setFilter({ ...filter, defectWebform: value })
                    }
                    value={filter.defectWebform || ''}
                  >
                    {WEBFORM_OPTIONS && WEBFORM_OPTIONS.map(o => (
                      <Select.Item key={o} value={o}>
                        {o}
                      </Select.Item>
                    ))}
                  </Select>
                </FlexGrid.Col>
                <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                  <Select
                    label={FILTER_BY_CATEGORY.title}
                    placeholder={FILTER_BY_CATEGORY.placeholder}
                    onChange={(value) =>
                      setFilter({
                        ...filter,
                        defectCategory: value
                      })
                    }
                    value={filter.defectCategory || ''}
                  >
                    {FILTER_BY_DEFECT_CATEGORY_OPTIONS && FILTER_BY_DEFECT_CATEGORY_OPTIONS.map(o => (
                      <Select.Item key={o} value={o}>
                        {o}
                      </Select.Item>
                    ))}
                  </Select>
                </FlexGrid.Col>
                <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                  <Select
                    label={FILTER_BY_STATUS.title}
                    placeholder={FILTER_BY_STATUS.placeholder}
                    onChange={(value) =>
                      setFilter({ ...filter, status: value })
                    }
                    value={filter.status || ''}
                  >
                    {STATUS_OPTIONS && STATUS_OPTIONS.map(o => (
                      <Select.Item key={o} value={o}>
                        {o}
                      </Select.Item>
                    ))}
                  </Select>
                </FlexGrid.Col>
                {role.name === 'ADMIN' ? (
                  <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                    <Select
                      label={FILTER_BY_ASSIGNEE.title}
                      placeholder={FILTER_BY_ASSIGNEE.placeholder}
                      onChange={(value) =>
                        setFilter({ ...filter, assignee: value })
                      }
                      value={filter.assignee || ''}
                    >
                      {assignedUsers && assignedUsers.map(o => (
                        <Select.Item key={o.email} value={o.email}>
                          {`${o.firstName} ${o.lastName}`}
                        </Select.Item>
                      ))}
                    </Select>
                  </FlexGrid.Col>
                ) : (
                  ''
                )}
                <FlexGrid.Col xs={12}>
                  <Box mt={16}>
                    <Typography variant={{ bold: true }}>{filterByReportedDate}</Typography>
                  </Box>
                </FlexGrid.Col>
                <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                  <div onFocus={() => window.dispatchEvent(new Event('scroll'))}>
                  <ManagedDatePicker
                    id="defect-from"
                    label={FROM}
                    value={filter.startDate || ''}
                    onValueChange={(v) => {
                      const isEndDateBeforeStartDate =
                        filter.endDate &&
                        v &&
                        isBefore(new Date(filter.endDate), new Date(v));
                      setFilter({
                        ...filter,
                        startDate: v,
                        ...(isEndDateBeforeStartDate && { endDate: '' })
                      });
                    }}
                    isDayDisabled={() => false}
                    dateFormat="YYYY-MM-DD"
                    initialVisibleMonth={moment().format('YYYY-MM-DD')}
                    copy={local === 'fr-CA' ? 'fr' : 'en'}
                  />
                  </div>
                </FlexGrid.Col>
                <FlexGrid.Col xs={12} md={3} lg={3} sm={12}>
                  <div onFocus={() => window.dispatchEvent(new Event('scroll'))}>
                  <ManagedDatePicker
                    id="defect-to"
                    label={TO}
                    isDayDisabled={day => filter.startDate ? day.isBefore(moment(filter.startDate, 'YYYY-MM-DD'), 'day') : false}
                    dateFormat="YYYY-MM-DD"
                    initialVisibleMonth={moment().format('YYYY-MM-DD')}
                    value={filter.endDate || ''}
                    onValueChange={(v) => {
                      setFilter({
                        ...filter,
                        endDate: v
                      });
                    }}
                    copy={local === 'fr-CA' ? 'fr' : 'en'}
                  />
                  </div>
                </FlexGrid.Col>
              </FlexGrid.Row>
              <Box
                display="flex"
                justifyContent="center"
                flexDirection="row"
                mt={32}
                mdJustifyContent="flex-start"
                mdWidth={100}
                mdPadding="16px 0"
                margin="0 auto"
              >
                <Button
                  variant={{ priority: 'low' }}
                  onPress={() => onApplyFilters(filter)}
                >
                  {APPLY}
                </Button>
                <Box width="2"> </Box>
                <Button
                  onPress={() => {
                    setFilter({ ...FILTERS_INIT_STATE });
                    onResetFilters(true);
                  }}
                >
                  {RESET}
                </Button>
              </Box>
            </FlexGrid>
          </StackView>
        </ExpandCollapse.Panel>
      )}
    </ExpandCollapse>
      {!!Object.values(defaultFilters).filter(value => !!value).length && (
        <>
          <Typography heading="h3" variant={{ size: 'h3' }} block>
            {Showing_Results_for}
          </Typography>
          <Tags
            copy={local === 'fr-CA' ? 'fr' : 'en'}
            items={Object.keys(defaultFilters)
              .filter(key => defaultFilters[key])
              .map(key => ({
                id: key,
                label: `${filterKeyToShortTitleMap[key]}: ${
                  ['startDate', 'endDate'].includes(key)
                    ? new Date(defaultFilters[key]).toDateString()
                    : defaultFilters[key]
                }`
              }))}
            values={Object.keys(defaultFilters).filter(key => defaultFilters[key])}
            onChange={(selectedIds) => {
              // Find which tag was removed
              const currentSelected = Object.keys(defaultFilters).filter(key => defaultFilters[key]);
              const removedId = currentSelected.find(id => !selectedIds.includes(id));
              if (removedId) {
                onRemoveTagFilter(removedId);
              }
            }}
          />
        </>
      )}
    </StackView>
  );
};

Filters.propTypes = {
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  role: PropTypes.shape({ name: PropTypes.string }).isRequired,
  assignedUsers: PropTypes.shape().isRequired,
  defaultFilters: PropTypes.shape().isRequired
};
export default Filters;
